import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Check, Clock, Euro, AlertCircle, Gift } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { CourseCalendar } from './CourseCalendar';
import { BookingForm } from './BookingForm';

export const CourseCard = ({ course }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const { toast } = useToast();

  const handleDateSelect = (dateInfo) => {
    setSelectedDate(dateInfo);
    setShowBookingForm(true);
  };

  const handleBookingSuccess = () => {
    // Keep dialog open to show calendar export options
    // Dialog will be manually closed by user
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setSelectedDate(null);
    setShowBookingForm(false);
  };

  return (
    <>
      <Card className="group h-full flex flex-col hover:shadow-2xl transition-all duration-300 border-2 border-[#D4AF37]/20 hover:border-[#D4AF37] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        
        <CardHeader className="relative z-10">
          <div className="flex items-start justify-between mb-3">
            <Badge className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-white border-0">
              {course.level}
            </Badge>
            <div className="flex items-center space-x-1 text-[#D4AF37] font-bold text-xl">
              {course.price > 0 ? (
                <>
                  <Euro className="w-5 h-5" />
                  <span>{course.price}</span>
                </>
              ) : (
                <span className="text-green-600 text-sm">Kostenlos</span>
              )}
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900 group-hover:text-[#D4AF37] transition-colors duration-300">
            {course.title}
          </CardTitle>
          <CardDescription className="flex items-center space-x-2 text-gray-600 mt-2">
            <Clock className="w-4 h-4" />
            <span>{course.duration}</span>
          </CardDescription>
        </CardHeader>

        <CardContent className="flex-grow relative z-10">
          <p className="text-gray-700 mb-4 leading-relaxed">
            {course.description}
          </p>

          <div className="space-y-2 mb-4">
            {course.highlights.slice(0, 3).map((highlight, index) => (
              <div key={index} className="flex items-start space-x-2">
                <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-600">{highlight}</span>
              </div>
            ))}
          </div>

          {course.price > 0 && (
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-3 mb-4">
              <div className="flex items-center space-x-2 mb-1">
                <Gift className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-sm font-semibold text-[#B8960C]">Buffet-Vorteil</span>
              </div>
              <p className="text-xs text-gray-600">
                Mit deinem Buffet-Beitrag: <span className="font-bold text-[#D4AF37]">{course.discountedPrice > 0 ? `${course.discountedPrice}€` : 'Kostenlos'}</span>
              </p>
            </div>
          )}
          {course.price === 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <div className="flex items-center space-x-2">
                <Gift className="w-4 h-4 text-green-600" />
                <span className="text-sm font-semibold text-green-800">Dein Buffet-Beitrag ist die Eintrittskarte</span>
              </div>
            </div>
          )}
        </CardContent>

        <CardFooter className="relative z-10">
          <Button
            onClick={() => setIsDialogOpen(true)}
            className="w-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] hover:from-[#B8960C] hover:to-[#D4AF37] text-white shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Jetzt buchen
          </Button>
        </CardFooter>
      </Card>

      {/* Booking Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="w-[95vw] max-w-[900px] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-2xl font-bold text-[#D4AF37] pr-8">{course.title}</DialogTitle>
            <DialogDescription className="text-sm sm:text-base">
              {!showBookingForm ? 'Wähle einen Termin aus' : 'Fülle das Anmeldeformular aus'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 sm:space-y-4 py-2 sm:py-4">
            {!showBookingForm ? (
              <CourseCalendar 
                courseId={course.id}
                courseTitle={course.title}
                onDateSelect={handleDateSelect}
              />
            ) : (
              <div className="space-y-3 sm:space-y-4">
                <Button
                  variant="outline"
                  onClick={() => setShowBookingForm(false)}
                  className="mb-2 sm:mb-4 text-sm"
                >
                  ← Zurück zur Terminauswahl
                </Button>
                <BookingForm
                  selectedDate={selectedDate}
                  courseTitle={course.title}
                  onSuccess={handleBookingSuccess}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};